import { HelpOutline } from '@mui/icons-material'
import { Card, Grid2, Stack, Tooltip, Typography } from '@mui/material'
import { useGetLiveData } from '@/services/hooks/relay/dynamic'
import { OrgCredential } from '@/types/credentials'
import { BasePaymentMethod } from '@/types/relay'
import { PAYMENT_METHOD_INFO_TYPES_IDS } from '@/utils/info-types'
import { useCredentialTypeContext } from './CredentialTypeProvider'
import PaymentMethodWithCardNumbers from './PaymentMethodWithCardNumbers'

type PaymentMethodsSettingsProps = {
  selectedCredential: OrgCredential
  settingsOrgId: string
  isAuthenticated: boolean
}

export default function PaymentMethodsSettings({
  selectedCredential,
  settingsOrgId,
  isAuthenticated,
}: PaymentMethodsSettingsProps) {
  const credentialType = useCredentialTypeContext()

  const { allData: paymentMethods } = useGetLiveData<BasePaymentMethod>({
    infoTypeId: PAYMENT_METHOD_INFO_TYPES_IDS[0], // Since it's used as a hint, it's okay to pass any of them
    orgId: settingsOrgId,
    orgCredentialTypeId: selectedCredential.type_id,
    enabled: isAuthenticated,
  })

  return (
    <Card sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="h6">Payment Method Card Numbers</Typography>
          <Tooltip
            title={`These are your payment method accounts as defined in ${credentialType.name}. If you want to add or remove a 
                    payment method account, please do so in ${credentialType.name}.`}
          >
            <HelpOutline sx={{ color: 'text.secondary' }} fontSize="small" />
          </Tooltip>
        </Stack>

        <Typography color="text.secondary" variant="body2">
          Card numbers are used to determine which payment method was used for a
          paid invoice or receipt. Pixydocs will automatically match the payment
          method found on the document when possible. Adding card numbers here
          increases the likelihood the automatic matching will succeed.
        </Typography>

        <Grid2 container spacing={1}>
          {paymentMethods.map((paymentMethod) => {
            return (
              <Grid2
                key={paymentMethod.entity_id}
                size={{
                  xs: 12,
                  sm: 6,
                  md: 4,
                }}
              >
                <PaymentMethodWithCardNumbers
                  orgId={settingsOrgId}
                  paymentMethod={paymentMethod}
                />
              </Grid2>
            )
          })}
        </Grid2>
      </Stack>
    </Card>
  )
}
